/* @ts-self-types="./fk_sim_wasm.d.ts" */

/**
 * A decoded snapshot plus the declaration the party view delivered, kept in wasm memory.
 */
export class SnapshotHandle {
    static __wrap(ptr) {
        const obj = Object.create(SnapshotHandle.prototype);
        obj.__wbg_ptr = ptr;
        SnapshotHandleFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        SnapshotHandleFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_snapshothandle_free(ptr, 0);
    }
    /**
     * @returns {bigint}
     */
    get encounter_id() {
        const ret = wasm.snapshothandle_encounter_id(this.__wbg_ptr);
        return BigInt.asUintN(64, ret);
    }
    /**
     * @returns {number}
     */
    get pulse_index() {
        const ret = wasm.snapshothandle_pulse_index(this.__wbg_ptr);
        return ret >>> 0;
    }
}
if (Symbol.dispose) SnapshotHandle.prototype[Symbol.dispose] = SnapshotHandle.prototype.free;

/**
 * docs/35 review: a room of wildlife kept in wasm, ticked like the module ticks it, for the art
 * gallery (`?gallery=1&wild=1`) where there is no server. `habitat` is `fk_wild::Habitat`
 * (`{ w, h, cells: ["Open" | "Wall" | …], bodies: [{ unit, x, y, size, hero }] }`), `room`
 * `fk_wild::Room` (`{ land: "Wood", kit, hour, weather: "Clear", intensity, season: "Summer" | null }`).
 */
export class WildPreview {
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        WildPreviewFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_wildpreview_free(ptr, 0);
    }
    /**
     * @param {any} habitat
     * @param {any} room
     * @param {bigint} seed
     */
    constructor(habitat, room, seed) {
        const ret = wasm.wildpreview_new(habitat, room, seed);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        this.__wbg_ptr = ret[0];
        WildPreviewFinalization.register(this, this.__wbg_ptr, this);
        return this;
    }
    /**
     * The creatures, as `wildlife` rows.
     * @returns {any}
     */
    rows() {
        const ret = wasm.wildpreview_rows(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Bodies moved (`[{ unit, x, y, size, hero }]`, where they stand now) at ms `at`.
     * @param {any} bodies
     * @param {number} at
     */
    stir(bodies, at) {
        const ret = wasm.wildpreview_stir(this.__wbg_ptr, bodies, at);
        if (ret[1]) {
            throw takeFromExternrefTable0(ret[0]);
        }
    }
    /**
     * One module tick at server ms `now`.
     * @param {number} now
     */
    tick(now) {
        wasm.wildpreview_tick(this.__wbg_ptr, now);
    }
}
if (Symbol.dispose) WildPreview.prototype[Symbol.dispose] = WildPreview.prototype.free;

/**
 * The ability catalogue for the hotbar (10 §4), as JS objects.
 * @returns {any}
 */
export function abilities() {
    const ret = wasm.abilities();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * An action's range for one unit at its `slot` (docs/48 AR1): the squares within reach from
 * where its earlier slots leave it, each `Clear`, `Blocked` or `TooFar`; `null` when the action
 * aims nowhere beyond the unit.
 * @param {SnapshotHandle} h
 * @param {number} unit
 * @param {any} slot
 * @param {number} action
 * @returns {any}
 */
export function aim(h, unit, slot, action) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.aim(h.__wbg_ptr, unit, slot, action);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/187 PK5: the floors and rails the module lays on a board with rooms (a crawl, a puzzle,
 * a dungeon) in `kit`, as `encounter_stage.look` carries them; empty for none.
 * `board` is a board as the forge's stages and handles give it out.
 * @param {any} board
 * @param {string} kit
 * @param {bigint} seed
 * @returns {string}
 */
export function board_look(board, kit, seed) {
    let deferred3_0;
    let deferred3_1;
    try {
        const ptr0 = passStringToWasm0(kit, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.board_look(board, ptr0, len0, seed);
        var ptr2 = ret[0];
        var len2 = ret[1];
        if (ret[3]) {
            ptr2 = 0; len2 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred3_0 = ptr2;
        deferred3_1 = len2;
        return getStringFromWasm0(ptr2, len2);
    } finally {
        wasm.__wbindgen_free(deferred3_0, deferred3_1, 1);
    }
}

/**
 * The creation choices of docs/59: every species, the feats, specialties, patrons and
 * companions (with their beasts' bodies), and the wizard's level-1 spell list.
 * @returns {any}
 */
export function callings() {
    const ret = wasm.callings();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The consumables Use Item can spend (docs/40 IU2): def id, name and what it does, from the
 * catalogue, so the in-fight inventory never keeps its own copy of the rules.
 * @returns {any}
 */
export function consumables() {
    const ret = wasm.consumables();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * What the gallery's card says about the crawl `crawl_stage` draws.
 * @param {bigint} seed
 * @param {any} vision
 * @param {number} heroes
 * @param {number} players
 * @returns {any}
 */
export function crawl_card(seed, vision, heroes, players) {
    const ret = wasm.crawl_card(seed, vision, heroes, players);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * A forged crawl for the gallery: seed, vision (`crawl_template_vision` or a storyteller's),
 * party size, and whether to begin the fog as the module does (then the handle holds the truth
 * and `fog_view` draws what the party sees).
 * @param {bigint} seed
 * @param {any} vision
 * @param {number} heroes
 * @param {number} players
 * @param {boolean} fogged
 * @returns {SnapshotHandle}
 */
export function crawl_stage(seed, vision, heroes, players, fogged) {
    const ret = wasm.crawl_stage(seed, vision, heroes, players, fogged);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return SnapshotHandle.__wrap(ret[0]);
}

/**
 * docs/60 DC2: the forge's own crawl vision from theme `theme` (as the module draws one where
 * no storyteller wrote it).
 * @param {bigint} seed
 * @param {number} theme
 * @returns {any}
 */
export function crawl_template_vision(seed, theme) {
    const ret = wasm.crawl_template_vision(seed, theme);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * How many themed banks the forge draws template crawl visions from.
 * @returns {number}
 */
export function crawl_theme_count() {
    const ret = wasm.crawl_theme_count();
    return ret >>> 0;
}

/**
 * @param {SnapshotHandle} h
 * @returns {any}
 */
export function declaration_view(h) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.declaration_view(h.__wbg_ptr);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Decode an `adventure_instance.blob` (postcard `fk_forge::AdventureBlob`) for the LocalMap
 * (11 §3.1 C2): sites, edges, outcome table, par minutes — the client never owns a second
 * definition of the site graph.
 * @param {Uint8Array} bytes
 * @returns {any}
 */
export function decode_adventure(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decode_adventure(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * A storybook chapter's scene (docs/86): `book_chapter.scene` (postcard `fk_schema::ChapterScene`,
 * its beats blanked by the module), as serde's JSON.
 * @param {Uint8Array} bytes
 * @returns {any}
 */
export function decode_chapter_scene(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decode_chapter_scene(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The World's story (docs/21): `world_story.genesis` (postcard `fk_schema::WorldGenesis`, the
 * arc's secret blanked by the module) as a plain object; enums are their variant names.
 * @param {Uint8Array} bytes
 * @returns {any}
 */
export function decode_genesis(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decode_genesis(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {Uint8Array} bytes
 * @returns {any}
 */
export function decode_intent(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decode_intent(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Decode a `ResolutionLog` blob delivered by the server for playback.
 * @param {Uint8Array} bytes
 * @returns {any}
 */
export function decode_log(bytes) {
    const ptr0 = passArray8ToWasm0(bytes, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.decode_log(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The P0 demo encounter (fixtures) for the client's offline mode and the parity harness.
 * @param {bigint} encounter_id
 * @returns {SnapshotHandle}
 */
export function demo_encounter(encounter_id) {
    const ret = wasm.demo_encounter(encounter_id);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return SnapshotHandle.__wrap(ret[0]);
}

/**
 * How many airs the template explorations are drawn in.
 * @returns {number}
 */
export function explore_air_count() {
    const ret = wasm.explore_air_count();
    return ret >>> 0;
}

/**
 * What the gallery's card says about the exploration `explore_stage` draws.
 * @param {bigint} seed
 * @param {any} vision
 * @param {number} heroes
 * @param {number} players
 * @returns {any}
 */
export function explore_card(seed, vision, heroes, players) {
    const ret = wasm.explore_card(seed, vision, heroes, players);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * A forged exploration for the gallery: seed, vision (`explore_template_vision` or a
 * storyteller's), party size, and whether to begin the fog as the module does. The board is
 * drawn under the air's sky (`fk_forge::explore::sky_of`), as the module stages it.
 * @param {bigint} seed
 * @param {any} vision
 * @param {number} heroes
 * @param {number} players
 * @param {boolean} fogged
 * @returns {SnapshotHandle}
 */
export function explore_stage(seed, vision, heroes, players, fogged) {
    const ret = wasm.explore_stage(seed, vision, heroes, players, fogged);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return SnapshotHandle.__wrap(ret[0]);
}

/**
 * docs/61 EX5: the forge's own exploration vision in air `air` (`fk_schema::Air` order; 255 any),
 * as the module draws one where no storyteller wrote it.
 * @param {bigint} seed
 * @param {number} air
 * @returns {any}
 */
export function explore_template_vision(seed, air) {
    const ret = wasm.explore_template_vision(seed, air);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/66 RM6: the fight recipes, each with the grounds that have it, for the gallery.
 * @returns {any}
 */
export function fight_recipes() {
    const ret = wasm.fight_recipes();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/103: the flank book's stage (`fk_sim::spellbook::flank_stage`), no intents yet.
 * @param {bigint} encounter_id
 * @param {boolean} misdirection
 * @returns {SnapshotHandle}
 */
export function flank_stage(encounter_id, misdirection) {
    const ret = wasm.flank_stage(encounter_id, misdirection);
    return SnapshotHandle.__wrap(ret);
}

/**
 * The band's first foe casting `kind` (`"bolt"`, `"burst"`, `"curse"`, `"mend"`) on a `foe_stage`.
 * @param {SnapshotHandle} h
 * @param {string} kind
 * @param {number} seed
 * @returns {any}
 */
export function foe_cast(h, kind, seed) {
    _assertClass(h, SnapshotHandle);
    const ptr0 = passStringToWasm0(kind, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.foe_cast(h.__wbg_ptr, ptr0, len0, seed);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The stage with the band rehosted as `kin`'s people (`"Goblin"`, `"Skeleton"`, `"Spider"` …).
 * @param {string} kin
 * @param {bigint} encounter_id
 * @returns {SnapshotHandle}
 */
export function foe_stage(kin, encounter_id) {
    const ptr0 = passStringToWasm0(kin, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.foe_stage(ptr0, len0, encounter_id);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return SnapshotHandle.__wrap(ret[0]);
}

/**
 * The fog of war as the party sees it (docs/31 J10): cells, lit squares, each hero's own sight,
 * the marks and the words; `null` with the fog off.
 * @param {SnapshotHandle} h
 * @returns {any}
 */
export function fog_view(h) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.fog_view(h.__wbg_ptr);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/31: the party's fog part-way through a pulse's playback — the board it began on (`h`)
 * with the `changed` squares as they stand after it (`end`), and the party where `at` puts it
 * (`[{ unit, tile: { x, y }, down }]`); `null` with the fog off.
 * @param {SnapshotHandle} h
 * @param {SnapshotHandle} end
 * @param {Uint32Array} changed
 * @param {any} at
 * @returns {any}
 */
export function fog_view_between(h, end, changed, at) {
    _assertClass(h, SnapshotHandle);
    _assertClass(end, SnapshotHandle);
    const ptr0 = passArray32ToWasm0(changed, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.fog_view_between(h.__wbg_ptr, end.__wbg_ptr, ptr0, len0, at);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * A coarse seeded board layout from the forge (05 §3.2, #166 split): `style` is one of
 * `dungeon | cavern | bog | forest`; the result is `{ style, kit, seed, attempt, board }` with the
 * board in the same serde shape as `snapshot_view().board`, for the client's view-tile ruleset
 * gallery and for offline boards. Unknown styles are an error.
 * @param {string} style
 * @param {bigint} seed
 * @param {number} w
 * @param {number} h
 * @returns {any}
 */
export function generate_board(style, seed, w, h) {
    const ptr0 = passStringToWasm0(style, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.generate_board(ptr0, len0, seed, w, h);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * `generate_board` cut to a seeded free-form outline (docs/38 BS2): the same result shape, its
 * board's squares outside the field `Void`, the rectangle cropped to what is left.
 * @param {string} style
 * @param {bigint} seed
 * @param {number} w
 * @param {number} h
 * @returns {any}
 */
export function generate_field(style, seed, w, h) {
    const ptr0 = passStringToWasm0(style, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.generate_field(ptr0, len0, seed, w, h);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/128 ME5: the single-target abilities (spells and items' fight uses) that may be aimed at
 * a bare square for what they bring to the ground there: `[id, …]`.
 * @returns {any}
 */
export function ground_aims() {
    const ret = wasm.ground_aims();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/76: the region kit a ground's fields are drawn in (the `?secrets=1` gallery's board).
 * @param {string} ground
 * @returns {string}
 */
export function ground_kit(ground) {
    let deferred2_0;
    let deferred2_1;
    try {
        const ptr0 = passStringToWasm0(ground, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.ground_kit(ptr0, len0);
        deferred2_0 = ret[0];
        deferred2_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
    }
}

/**
 * The intents a staged handle carries (a gallery stage's planned cast).
 * @param {SnapshotHandle} h
 * @returns {any}
 */
export function handle_intents(h) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.handle_intents(h.__wbg_ptr);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * One item at one tier as a shop or market shows it: its slot, who may use it, the numbers it
 * adds (10 §6.4), the trait it gives, and what it does if it is used up. `None` (JS `undefined`)
 * for an unknown def or a tier it does not come in.
 * @param {number} def
 * @param {number} tier
 * @returns {any}
 */
export function item_card(def, tier) {
    const ret = wasm.item_card(def, tier);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * `legal` against the current intent set (04 §5.2).
 * @param {SnapshotHandle} h
 * @param {any} candidate
 * @returns {any}
 */
export function legal(h, candidate) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.legal(h.__wbg_ptr, candidate);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * `legal` for many candidates (docs/118 PT13): the verdicts in order, the plan run once per
 * (unit, slot) instead of once per candidate.
 * @param {SnapshotHandle} h
 * @param {any} candidates
 * @returns {any}
 */
export function legal_many(h, candidates) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.legal_many(h.__wbg_ptr, candidates);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The squares a tile-aimed `base` may legally be aimed at (docs/118 PT13), as flat `[x, y, …]`:
 * from `among` (flat `[x, y, …]`) when given, else from the squares the action could land on.
 * @param {SnapshotHandle} h
 * @param {any} base
 * @param {Int32Array | null} [among]
 * @returns {Int32Array}
 */
export function legal_tiles(h, base, among) {
    _assertClass(h, SnapshotHandle);
    var ptr0 = isLikeNone(among) ? 0 : passArray32ToWasm0(among, wasm.__wbindgen_malloc);
    var len0 = WASM_VECTOR_LEN;
    const ret = wasm.legal_tiles(h.__wbg_ptr, base, ptr0, len0);
    if (ret[3]) {
        throw takeFromExternrefTable0(ret[2]);
    }
    var v2 = getArrayI32FromWasm0(ret[0], ret[1]).slice();
    wasm.__wbindgen_free(ret[0], ret[1] * 4, 4);
    return v2;
}

/**
 * Decode once (`postcard`), keep in wasm memory (11 §6.1).
 * @param {Uint8Array} snapshot
 * @param {Uint8Array} declaration
 * @returns {SnapshotHandle}
 */
export function load_snapshot(snapshot, declaration) {
    const ptr0 = passArray8ToWasm0(snapshot, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray8ToWasm0(declaration, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.load_snapshot(ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return SnapshotHandle.__wrap(ret[0]);
}

/**
 * Build an intent from JS fields and return it as a JS object with the current snapshot hash
 * filled in, plus its postcard bytes for the reducer call.
 * @param {SnapshotHandle} h
 * @param {bigint} hero_id
 * @param {number} slot
 * @param {number} action
 * @param {any} args
 * @param {number} revision
 * @returns {any}
 */
export function make_intent(h, hero_id, slot, action, args, revision) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.make_intent(h.__wbg_ptr, hero_id, slot, action, args, revision);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Every row of the interaction table: `{id, on, by, say, look}`.
 * @returns {any}
 */
export function matter_book() {
    const ret = wasm.matter_book();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The matter book's scenes: `[[key, title], …]`.
 * @returns {any}
 */
export function matter_scenes() {
    const ret = wasm.matter_scenes();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The stage for matter scene `key`, its cast already planned (the handle's one intent).
 * @param {string} key
 * @param {bigint} encounter_id
 * @returns {SnapshotHandle}
 */
export function matter_stage(key, encounter_id) {
    const ptr0 = passStringToWasm0(key, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.matter_stage(ptr0, len0, encounter_id);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return SnapshotHandle.__wrap(ret[0]);
}

/**
 * The names the sim gives every surface, cloud and thing (docs/114 MT8), for the hover chip.
 * @returns {any}
 */
export function matter_words() {
    const ret = wasm.matter_words();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Every enemy's mind in the sim's words (docs/30): its people, wits, mood, quirks, gear and the
 * cards in its deck by count — what the inspect card shows.
 * @param {SnapshotHandle} h
 * @returns {any}
 */
export function minds(h) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.minds(h.__wbg_ptr);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The foes' move vocabulary (docs/53), in id order.
 * @returns {any}
 */
export function move_book() {
    const ret = wasm.move_book();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The stage's boss performing move `id` on the real engine, and the director's cues for it.
 * @param {SnapshotHandle} h
 * @param {number} id
 * @param {number} seed
 * @returns {any}
 */
export function move_cast(h, id, seed) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.move_cast(h.__wbg_ptr, id, seed);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The move book's stage for move `id`, its boss dressed as `kin` (or the first people the move
 * fits when `kin` does not).
 * @param {number} id
 * @param {string} kin
 * @param {bigint} encounter_id
 * @returns {SnapshotHandle}
 */
export function move_stage(id, kin, encounter_id) {
    const ptr0 = passStringToWasm0(kin, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.move_stage(id, ptr0, len0, encounter_id);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return SnapshotHandle.__wrap(ret[0]);
}

/**
 * The hands a thing answers (`["Push", "Work"]`), lit or not (docs/114 MT4).
 * @param {string} kind
 * @param {boolean} lit
 * @returns {any}
 */
export function object_verbs(kind, lit) {
    const ptr0 = passStringToWasm0(kind, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.object_verbs(ptr0, len0, lit);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The background and loadout catalogue (`fk_grammar::origins`), so the client never keeps a
 * second copy of what a hero can start with.
 * @returns {any}
 */
export function origins() {
    const ret = wasm.origins();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {SnapshotHandle} h
 * @returns {string}
 */
export function plan_hash(h) {
    let deferred1_0;
    let deferred1_1;
    try {
        _assertClass(h, SnapshotHandle);
        const ret = wasm.plan_hash(h.__wbg_ptr);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * What a new hero would be at `level`: `derive_stats` over the exact kit `forge_hero` grants,
 * with the calling chosen (docs/59: `pick` a calling id, `spells` a wizard's three).
 * @param {number} _class
 * @param {number} species
 * @param {number} build
 * @param {number} background
 * @param {number} loadout
 * @param {number} level
 * @param {number} pick
 * @param {Uint16Array} spells
 * @returns {any}
 */
export function preview_hero(_class, species, build, background, loadout, level, pick, spells) {
    const ptr0 = passArray16ToWasm0(spells, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.preview_hero(_class, species, build, background, loadout, level, pick, ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/71 INV8: what a hero's numbers are wearing exactly `worn` (flat `[slot, def, tier, …]`
 * triples), with its level cards and calling: `derive_stats` as the module's load runs it, for
 * the inventory's numbers and its "if you wore this" comparison. docs/112: `upgrades` is each
 * worn piece's upgrades in the same order, `[n, id × n, …]` (empty: none on any).
 * @param {number} _class
 * @param {number} species
 * @param {number} build
 * @param {number} level
 * @param {number} pick
 * @param {Uint16Array} spells
 * @param {Uint16Array} cards
 * @param {Uint16Array} worn
 * @param {Uint16Array} upgrades
 * @param {Uint16Array} picks
 * @param {Uint8Array} training
 * @param {Uint16Array} talents
 * @param {Uint16Array} laurels
 * @returns {any}
 */
export function preview_hero_gear(_class, species, build, level, pick, spells, cards, worn, upgrades, picks, training, talents, laurels) {
    const ptr0 = passArray16ToWasm0(spells, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray16ToWasm0(cards, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray16ToWasm0(worn, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray16ToWasm0(upgrades, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ptr4 = passArray16ToWasm0(picks, wasm.__wbindgen_malloc);
    const len4 = WASM_VECTOR_LEN;
    const ptr5 = passArray8ToWasm0(training, wasm.__wbindgen_malloc);
    const len5 = WASM_VECTOR_LEN;
    const ptr6 = passArray16ToWasm0(talents, wasm.__wbindgen_malloc);
    const len6 = WASM_VECTOR_LEN;
    const ptr7 = passArray16ToWasm0(laurels, wasm.__wbindgen_malloc);
    const len7 = WASM_VECTOR_LEN;
    const ret = wasm.preview_hero_gear(_class, species, build, level, pick, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4, ptr5, len5, ptr6, len6, ptr7, len7);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/113: a library quest walked as a path, for the `?path=1` gallery: a side quest (or, `main`,
 * a main quest's longer path) from `seed`, standing in `scene` (a `PuzzleGround` name), or the
 * scene its own words give it when `scene` is empty.
 * @param {number} seed
 * @param {boolean} main
 * @param {string} scene
 * @returns {any}
 */
export function preview_quest_path(seed, main, scene) {
    const ptr0 = passStringToWasm0(scene, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.preview_quest_path(seed, main, ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/119: the whole progression catalogue — every class track, species heritage, training
 * track, talent, laurel and feature, and every level ability's look — for the level-up and path
 * screens.
 * @returns {any}
 */
export function progression() {
    const ret = wasm.progression();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/119: a hero's walk through levels 2–20 (every slot, its options and what it holds; the
 * levels still to come as their defaults), what levelling grants them at `level`, their skill
 * points and every ability they own. `picks` is the stored `[level, slot, value, …]`.
 * @param {number} _class
 * @param {number} species
 * @param {number} level
 * @param {number} pick
 * @param {Uint16Array} spells
 * @param {Uint16Array} picks
 * @param {Uint8Array} training
 * @param {Uint16Array} talents
 * @param {Uint16Array} laurels
 * @returns {any}
 */
export function progression_walk(_class, species, level, pick, spells, picks, training, talents, laurels) {
    const ptr0 = passArray16ToWasm0(spells, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passArray16ToWasm0(picks, wasm.__wbindgen_malloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passArray8ToWasm0(training, wasm.__wbindgen_malloc);
    const len2 = WASM_VECTOR_LEN;
    const ptr3 = passArray16ToWasm0(talents, wasm.__wbindgen_malloc);
    const len3 = WASM_VECTOR_LEN;
    const ptr4 = passArray16ToWasm0(laurels, wasm.__wbindgen_malloc);
    const len4 = WASM_VECTOR_LEN;
    const ret = wasm.progression_walk(_class, species, level, pick, ptr0, len0, ptr1, len1, ptr2, len2, ptr3, len3, ptr4, len4);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * `project` with the current intent set (04 §5.4).
 * @param {SnapshotHandle} h
 * @returns {any}
 */
export function project(h) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.project(h.__wbg_ptr);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * What the gallery's card says about the room `puzzle_stage` draws.
 * @param {bigint} seed
 * @param {any} vision
 * @param {number} heroes
 * @returns {any}
 */
export function puzzle_card(seed, vision, heroes) {
    const ret = wasm.puzzle_card(seed, vision, heroes);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * A forged puzzle room for the gallery: seed, the vision it grows from (docs/57: a storyteller
 * example or `puzzle_template_vision`), party size, and how many pulses of its proven solution
 * to play first (0: as the party finds it). The answer is in the snapshot: this is a design
 * tool, not the game.
 * @param {bigint} seed
 * @param {any} vision
 * @param {number} heroes
 * @param {number} pulses
 * @param {boolean} fogged
 * @returns {SnapshotHandle}
 */
export function puzzle_stage(seed, vision, heroes, pulses, fogged) {
    const ret = wasm.puzzle_stage(seed, vision, heroes, pulses, fogged);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return SnapshotHandle.__wrap(ret[0]);
}

/**
 * docs/57: the forge's own vision on ground `ground` (`PuzzleGround::ALL` order), as the
 * module draws one where no storyteller wrote it.
 * @param {bigint} seed
 * @param {number} ground
 * @returns {any}
 */
export function puzzle_template_vision(seed, ground) {
    const ret = wasm.puzzle_template_vision(seed, ground);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Realm preview placeholder (#305): applies a delta to a ledger (09 P0-R1).
 * @param {any} ledger
 * @param {any} delta
 * @returns {any}
 */
export function realm_preview(ledger, delta) {
    const ret = wasm.realm_preview(ledger, delta);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Dev parity only: the full resolution of the current intents as the server would compute it.
 * @param {SnapshotHandle} h
 * @returns {any}
 */
export function resolve(h) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.resolve(h.__wbg_ptr);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/149 MP: the squares a walk to `(x, y)` takes in each pulse from now, leg 0 this pulse.
 * `null` when the walk cannot be made.
 * @param {SnapshotHandle} h
 * @param {number} unit
 * @param {number} x
 * @param {number} y
 * @returns {any}
 */
export function route_legs(h, unit, x, y) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.route_legs(h.__wbg_ptr, unit, x, y);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @returns {number}
 */
export function ruleset_version() {
    const ret = wasm.ruleset_version();
    return ret >>> 0;
}

/**
 * docs/76: a staged fight with every hidden thing planted as `dev_plant_secrets` plants them
 * (a hidden way, a clue, traps, a hidden room in an outcrop and a cellar's stairway), for the
 * `?secrets=1` gallery. `reveal` 0 gives the party's view of the fresh field, 1 the field after
 * a Scroll of Revealing, 2 with every hidden door opened as well (the truth stays in the handle
 * only for 1 and 2, where nothing is left hidden).
 * @param {string} ground
 * @param {number} heroes
 * @param {bigint} seed
 * @param {number} reveal
 * @returns {SnapshotHandle}
 */
export function secrets_stage(ground, heroes, seed, reveal) {
    const ptr0 = passStringToWasm0(ground, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.secrets_stage(ptr0, len0, heroes, seed, reveal);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return SnapshotHandle.__wrap(ret[0]);
}

/**
 * Replace the current intent set (party rows + local edits), each as postcard bytes
 * concatenated with a u32 LE length prefix, or as a JS array of intent objects.
 * @param {SnapshotHandle} h
 * @param {any} intents
 * @returns {number}
 */
export function set_intents(h, intents) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.set_intents(h.__wbg_ptr, intents);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] >>> 0;
}

/**
 * The server's hash for this snapshot (64 hex chars), when it differs from the bytes' own: a
 * fogged party view (docs/31 J8). Intents made here then carry it, and `legal` accepts it.
 * @param {SnapshotHandle} h
 * @param {string} hex
 */
export function set_served_hash(h, hex) {
    _assertClass(h, SnapshotHandle);
    const ptr0 = passStringToWasm0(hex, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.set_served_hash(h.__wbg_ptr, ptr0, len0);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

/**
 * @param {SnapshotHandle} h
 * @returns {string}
 */
export function snapshot_hash(h) {
    let deferred1_0;
    let deferred1_1;
    try {
        _assertClass(h, SnapshotHandle);
        const ret = wasm.snapshot_hash(h.__wbg_ptr);
        deferred1_0 = ret[0];
        deferred1_1 = ret[1];
        return getStringFromWasm0(ret[0], ret[1]);
    } finally {
        wasm.__wbindgen_free(deferred1_0, deferred1_1, 1);
    }
}

/**
 * The snapshot as a JS object (board, units, kit names…) for the renderer.
 * @param {SnapshotHandle} h
 * @returns {any}
 */
export function snapshot_view(h) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.snapshot_view(h.__wbg_ptr);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/140 DT1: the hundred traps of the book, in id order.
 * @returns {any}
 */
export function snare_book() {
    const ret = wasm.snare_book();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/140 DT12: the demo crawl for book entry `def` with `heroes` heroes (1–4), its trap armed.
 * @param {number} def
 * @param {number} heroes
 * @returns {SnapshotHandle}
 */
export function snare_demo(def, heroes) {
    const ret = wasm.snare_demo(def, heroes);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return SnapshotHandle.__wrap(ret[0]);
}

/**
 * docs/140 DT12: plays book entry `def` on its demo crawl — springs it on the first hero (by its
 * foe for a card or a fall) and runs up to `pulses` pulses' Events stages after it, stopping once
 * it is spent. The gallery plays each frame through the real playback.
 * @param {number} def
 * @param {number} heroes
 * @param {number} pulses
 * @returns {any}
 */
export function snare_demo_run(def, heroes, pulses) {
    const ret = wasm.snare_demo_run(def, heroes, pulses);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Casts the stage's spell: the party's half of the pulse (the band does not answer) and the cues
 * the module's director scores for it, `seed` choosing the variants as a pulse's seed would.
 * @param {SnapshotHandle} h
 * @param {number} seed
 * @returns {any}
 */
export function spell_cast(h, seed) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.spell_cast(h.__wbg_ptr, seed);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The stage for ability `ability`: a level-10 caster of its class, its friends and a band of
 * goblins on an open field, the cast already planned (the handle's one intent).
 * @param {number} ability
 * @param {bigint} encounter_id
 * @returns {SnapshotHandle}
 */
export function spell_stage(ability, encounter_id) {
    const ret = wasm.spell_stage(ability, encounter_id);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return SnapshotHandle.__wrap(ret[0]);
}

/**
 * Every class ability the spellbook page shows, in catalogue order.
 * @returns {any}
 */
export function spellbook() {
    const ret = wasm.spellbook();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/66 RM12: a fight's field as the forge stages it, for the gallery's setup and recipe
 * picker: `setup` one of `Setup::key` (Clash, Defend…), `recipe` a `Recipe::key`
 * (`river-crossing`…) or empty to draw one as a fight does, `ground` a `Ground` name (Mire,
 * Greenwood…). The result is the whole `deploy::Stage`: board, hero spots, leader tile, zones,
 * entries (with their names), the recipe, what it painted, its frame and how it was proved.
 * @param {string} setup
 * @param {string} recipe
 * @param {string} ground
 * @param {number} heroes
 * @param {number} players
 * @param {bigint} seed
 * @returns {any}
 */
export function stage_fight(setup, recipe, ground, heroes, players, seed) {
    const ptr0 = passStringToWasm0(setup, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(recipe, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(ground, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.stage_fight(ptr0, len0, ptr1, len1, ptr2, len2, heroes, players, seed);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/187 PK2: a fight's field in a place as the forge stages it, for the gallery and the
 * offline painter: `place` a place kit (`town`, `docks`, `courtyard`, `graveyard`, `ruins`,
 * `house`, `ship`, `barrow`, `mine`, `ember`), `ground` the country round it (a `Ground` name;
 * empty for green woods). The whole `deploy::Stage`, with its `kit`, `country` and `look`.
 * @param {string} setup
 * @param {string} place
 * @param {string} ground
 * @param {number} heroes
 * @param {number} players
 * @param {bigint} seed
 * @returns {any}
 */
export function stage_place(setup, place, ground, heroes, players, seed) {
    const ptr0 = passStringToWasm0(setup, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(place, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(ground, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.stage_place(ptr0, len0, ptr1, len1, ptr2, len2, heroes, players, seed);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Every unit's square as one unit's `slot` intent begins (docs/81 SA3): where a target is aimed
 * at — a friend's planning ghost, a foe the party will already have dropped (`fallen`).
 * @param {SnapshotHandle} h
 * @param {number} unit
 * @param {any} slot
 * @returns {any}
 */
export function stands(h, unit, slot) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.stands(h.__wbg_ptr, unit, slot);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The field for one unit at its `slot` (docs/26 F8): where it can stop and by which route, the
 * zones of control gripping it, its sight, and every square's class with a legend.
 * @param {SnapshotHandle} h
 * @param {number} unit
 * @param {any} slot
 * @returns {any}
 */
export function survey(h, unit, slot) {
    _assertClass(h, SnapshotHandle);
    const ret = wasm.survey(h.__wbg_ptr, unit, slot);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The template story for a seed phrase (docs/21 §3): the no-server gallery's sample genesis.
 * @param {string} seed_phrase
 * @param {string} world_name
 * @returns {any}
 */
export function template_genesis(seed_phrase, world_name) {
    const ptr0 = passStringToWasm0(seed_phrase, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(world_name, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.template_genesis(ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/189 EO2: every thing's shape: `[{ key, w, h, solid, wall, flat, hangs, lamp, flammable,
 * rolls, material }]`, in `ObjectKind` order.
 * @returns {any}
 */
export function thing_shapes() {
    const ret = wasm.thing_shapes();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * docs/112 UP2/UP5/UP7: for a piece of kind `def` carrying `applied`, what it takes
 * (`kinds`: 1 enchant, 2 improve) and, for every upgrade that fits it, the rank applying it
 * would reach and its price (before a haggler's share), or why it cannot go on. The rules'
 * own `can_apply` and `upgrade_price`, so the counter never disagrees with `upgrade_item`.
 * @param {number} def
 * @param {Uint16Array} applied
 * @returns {any}
 */
export function upgrade_options(def, applied) {
    const ptr0 = passArray16ToWasm0(applied, wasm.__wbindgen_malloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.upgrade_options(def, ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The species names by code (docs/35), for review captions.
 * @returns {any}
 */
export function wild_species() {
    const ret = wasm.wild_species();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}
function __wbg_get_imports() {
    const import0 = {
        __proto__: null,
        __wbg_Error_30c8987f7c2ed4e2: function(arg0, arg1) {
            const ret = Error(getStringFromWasm0(arg0, arg1));
            return ret;
        },
        __wbg_Number_14af1003b8dd5ead: function(arg0) {
            const ret = Number(arg0);
            return ret;
        },
        __wbg_String_8564e559799eccda: function(arg0, arg1) {
            const ret = String(arg1);
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_bigint_get_as_i64_a2383202b9353e4c: function(arg0, arg1) {
            const v = arg1;
            const ret = typeof(v) === 'bigint' ? v : undefined;
            getDataViewMemory0().setBigInt64(arg0 + 8 * 1, isLikeNone(ret) ? BigInt(0) : ret, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, !isLikeNone(ret), true);
        },
        __wbg___wbindgen_boolean_get_5b446f51afd21013: function(arg0) {
            const v = arg0;
            const ret = typeof(v) === 'boolean' ? v : undefined;
            return isLikeNone(ret) ? 0xFFFFFF : ret ? 1 : 0;
        },
        __wbg___wbindgen_debug_string_4687d8d8c2017d52: function(arg0, arg1) {
            const ret = debugString(arg1);
            const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            const len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_in_92f62ee1427d9e49: function(arg0, arg1) {
            const ret = arg0 in arg1;
            return ret;
        },
        __wbg___wbindgen_is_bigint_b123553bed3bb382: function(arg0) {
            const ret = typeof(arg0) === 'bigint';
            return ret;
        },
        __wbg___wbindgen_is_function_1f9d30630b8b1d3d: function(arg0) {
            const ret = typeof(arg0) === 'function';
            return ret;
        },
        __wbg___wbindgen_is_null_e343b7d08827ba72: function(arg0) {
            const ret = arg0 === null;
            return ret;
        },
        __wbg___wbindgen_is_object_3c45d4f2dde4e749: function(arg0) {
            const val = arg0;
            const ret = typeof(val) === 'object' && val !== null;
            return ret;
        },
        __wbg___wbindgen_is_string_90b56bc79aad6f6c: function(arg0) {
            const ret = typeof(arg0) === 'string';
            return ret;
        },
        __wbg___wbindgen_is_undefined_8865fb403f8fe9d8: function(arg0) {
            const ret = arg0 === undefined;
            return ret;
        },
        __wbg___wbindgen_jsval_eq_02babf21faa37971: function(arg0, arg1) {
            const ret = arg0 === arg1;
            return ret;
        },
        __wbg___wbindgen_jsval_loose_eq_677f21e468d6b461: function(arg0, arg1) {
            const ret = arg0 == arg1;
            return ret;
        },
        __wbg___wbindgen_number_get_2e0e7dee9f701a71: function(arg0, arg1) {
            const obj = arg1;
            const ret = typeof(obj) === 'number' ? obj : undefined;
            getDataViewMemory0().setFloat64(arg0 + 8 * 1, isLikeNone(ret) ? 0 : ret, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, !isLikeNone(ret), true);
        },
        __wbg___wbindgen_string_get_0380ccaa2f57f0d9: function(arg0, arg1) {
            const obj = arg1;
            const ret = typeof(obj) === 'string' ? obj : undefined;
            var ptr1 = isLikeNone(ret) ? 0 : passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
            var len1 = WASM_VECTOR_LEN;
            getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
            getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
        },
        __wbg___wbindgen_throw_41e9ee4f547fc59a: function(arg0, arg1) {
            throw new Error(getStringFromWasm0(arg0, arg1));
        },
        __wbg_call_6137034ef55c9d0f: function() { return handleError(function (arg0, arg1) {
            const ret = arg0.call(arg1);
            return ret;
        }, arguments); },
        __wbg_done_b41a1d26cdb37fb6: function(arg0) {
            const ret = arg0.done;
            return ret;
        },
        __wbg_entries_fb6397112b1de25f: function(arg0) {
            const ret = Object.entries(arg0);
            return ret;
        },
        __wbg_get_658f6698067d9515: function() { return handleError(function (arg0, arg1) {
            const ret = Reflect.get(arg0, arg1);
            return ret;
        }, arguments); },
        __wbg_get_6c896e0571ddae51: function(arg0, arg1) {
            const ret = arg0[arg1 >>> 0];
            return ret;
        },
        __wbg_get_unchecked_288889d017702237: function(arg0, arg1) {
            const ret = arg0[arg1 >>> 0];
            return ret;
        },
        __wbg_get_with_ref_key_6412cf3094599694: function(arg0, arg1) {
            const ret = arg0[arg1];
            return ret;
        },
        __wbg_instanceof_ArrayBuffer_a99f175873e5d9b8: function(arg0) {
            let result;
            try {
                result = arg0 instanceof ArrayBuffer;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_instanceof_Uint8Array_828cef2aaacafc31: function(arg0) {
            let result;
            try {
                result = arg0 instanceof Uint8Array;
            } catch (_) {
                result = false;
            }
            const ret = result;
            return ret;
        },
        __wbg_isArray_e15a2ff68ffdbef2: function(arg0) {
            const ret = Array.isArray(arg0);
            return ret;
        },
        __wbg_isSafeInteger_717808ad6a54bd9e: function(arg0) {
            const ret = Number.isSafeInteger(arg0);
            return ret;
        },
        __wbg_iterator_e3c31c892080e444: function() {
            const ret = Symbol.iterator;
            return ret;
        },
        __wbg_length_7f3c00c40364105e: function(arg0) {
            const ret = arg0.length;
            return ret;
        },
        __wbg_length_d4bdea10311bd9cf: function(arg0) {
            const ret = arg0.length;
            return ret;
        },
        __wbg_new_1dbf7428bba60a42: function(arg0) {
            const ret = new Uint8Array(arg0);
            return ret;
        },
        __wbg_new_617a8cdb8bb1130e: function() {
            const ret = new Object();
            return ret;
        },
        __wbg_new_ee2291f50781bf1d: function() {
            const ret = new Array();
            return ret;
        },
        __wbg_next_33784799010f1bbe: function(arg0) {
            const ret = arg0.next;
            return ret;
        },
        __wbg_next_f4aac29c42af995c: function() { return handleError(function (arg0) {
            const ret = arg0.next();
            return ret;
        }, arguments); },
        __wbg_prototypesetcall_bc27214492979395: function(arg0, arg1, arg2) {
            Uint8Array.prototype.set.call(getArrayU8FromWasm0(arg0, arg1), arg2);
        },
        __wbg_set_6be42768c690e380: function(arg0, arg1, arg2) {
            arg0[arg1] = arg2;
        },
        __wbg_set_bea140a88be9b277: function(arg0, arg1, arg2) {
            arg0[arg1 >>> 0] = arg2;
        },
        __wbg_value_f3c585ee8f5ba40c: function(arg0) {
            const ret = arg0.value;
            return ret;
        },
        __wbindgen_generic_0000000000000001: function(arg0) {
            // Cast intrinsic for `F64 -> Externref`.
            const ret = arg0;
            return ret;
        },
        __wbindgen_generic_0000000000000002: function(arg0) {
            // Cast intrinsic for `I64 -> Externref`.
            const ret = arg0;
            return ret;
        },
        __wbindgen_generic_0000000000000003: function(arg0, arg1) {
            // Cast intrinsic for `Ref(String) -> Externref`.
            const ret = getStringFromWasm0(arg0, arg1);
            return ret;
        },
        __wbindgen_generic_0000000000000004: function(arg0) {
            // Cast intrinsic for `U64 -> Externref`.
            const ret = BigInt.asUintN(64, arg0);
            return ret;
        },
        __wbindgen_init_externref_table: function() {
            const table = wasm.__wbindgen_externrefs;
            const offset = table.grow(4);
            table.set(0, undefined);
            table.set(offset + 0, undefined);
            table.set(offset + 1, null);
            table.set(offset + 2, true);
            table.set(offset + 3, false);
        },
    };
    return {
        __proto__: null,
        "./fk_sim_wasm_bg.js": import0,
    };
}

const SnapshotHandleFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_snapshothandle_free(ptr, 1));
const WildPreviewFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_wildpreview_free(ptr, 1));

function addToExternrefTable0(obj) {
    const idx = wasm.__externref_table_alloc();
    wasm.__wbindgen_externrefs.set(idx, obj);
    return idx;
}

function _assertClass(instance, klass) {
    if (!(instance instanceof klass)) {
        throw new Error(`expected instance of ${klass.name}`);
    }
}

function debugString(val) {
    // primitive types
    const type = typeof val;
    if (type == 'number' || type == 'boolean' || val == null) {
        return  `${val}`;
    }
    if (type == 'string') {
        return `"${val}"`;
    }
    if (type == 'symbol') {
        const description = val.description;
        if (description == null) {
            return 'Symbol';
        } else {
            return `Symbol(${description})`;
        }
    }
    if (type == 'function') {
        const name = val.name;
        if (typeof name == 'string' && name.length > 0) {
            return `Function(${name})`;
        } else {
            return 'Function';
        }
    }
    // objects
    if (Array.isArray(val)) {
        const length = val.length;
        let debug = '[';
        if (length > 0) {
            debug += debugString(val[0]);
        }
        for(let i = 1; i < length; i++) {
            debug += ', ' + debugString(val[i]);
        }
        debug += ']';
        return debug;
    }
    // Test for built-in
    const builtInMatches = /\[object ([^\]]+)\]/.exec(toString.call(val));
    let className;
    if (builtInMatches && builtInMatches.length > 1) {
        className = builtInMatches[1];
    } else {
        // Failed to match the standard '[object ClassName]'
        return toString.call(val);
    }
    if (className == 'Object') {
        // we're a user defined class or Object
        // JSON.stringify avoids problems with cycles, and is generally much
        // easier than looping through ownProperties of `val`.
        try {
            return 'Object(' + JSON.stringify(val) + ')';
        } catch (_) {
            return 'Object';
        }
    }
    // errors
    if (val instanceof Error) {
        return `${val.name}: ${val.message}\n${val.stack}`;
    }
    // TODO we could test for more things here, like `Set`s and `Map`s.
    return className;
}

function getArrayI32FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getInt32ArrayMemory0().subarray(ptr / 4, ptr / 4 + len);
}

function getArrayU8FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint8ArrayMemory0().subarray(ptr / 1, ptr / 1 + len);
}

let cachedDataViewMemory0 = null;
function getDataViewMemory0() {
    if (cachedDataViewMemory0 === null || cachedDataViewMemory0.buffer.detached === true || (cachedDataViewMemory0.buffer.detached === undefined && cachedDataViewMemory0.buffer !== wasm.memory.buffer)) {
        cachedDataViewMemory0 = new DataView(wasm.memory.buffer);
    }
    return cachedDataViewMemory0;
}

let cachedInt32ArrayMemory0 = null;
function getInt32ArrayMemory0() {
    if (cachedInt32ArrayMemory0 === null || cachedInt32ArrayMemory0.byteLength === 0) {
        cachedInt32ArrayMemory0 = new Int32Array(wasm.memory.buffer);
    }
    return cachedInt32ArrayMemory0;
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint16ArrayMemory0 = null;
function getUint16ArrayMemory0() {
    if (cachedUint16ArrayMemory0 === null || cachedUint16ArrayMemory0.byteLength === 0) {
        cachedUint16ArrayMemory0 = new Uint16Array(wasm.memory.buffer);
    }
    return cachedUint16ArrayMemory0;
}

let cachedUint32ArrayMemory0 = null;
function getUint32ArrayMemory0() {
    if (cachedUint32ArrayMemory0 === null || cachedUint32ArrayMemory0.byteLength === 0) {
        cachedUint32ArrayMemory0 = new Uint32Array(wasm.memory.buffer);
    }
    return cachedUint32ArrayMemory0;
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function handleError(f, args) {
    try {
        return f.apply(this, args);
    } catch (e) {
        const idx = addToExternrefTable0(e);
        wasm.__wbindgen_exn_store(idx);
    }
}

function isLikeNone(x) {
    return x === undefined || x === null;
}

function passArray16ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 2, 2) >>> 0;
    getUint16ArrayMemory0().set(arg, ptr / 2);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArray32ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 4, 4) >>> 0;
    getUint32ArrayMemory0().set(arg, ptr / 4);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passArray8ToWasm0(arg, malloc) {
    const ptr = malloc(arg.length * 1, 1) >>> 0;
    getUint8ArrayMemory0().set(arg, ptr / 1);
    WASM_VECTOR_LEN = arg.length;
    return ptr;
}

function passStringToWasm0(arg, malloc, realloc) {
    if (realloc === undefined) {
        const buf = cachedTextEncoder.encode(arg);
        const ptr = malloc(buf.length, 1) >>> 0;
        getUint8ArrayMemory0().subarray(ptr, ptr + buf.length).set(buf);
        WASM_VECTOR_LEN = buf.length;
        return ptr;
    }

    let len = arg.length;
    let ptr = malloc(len, 1) >>> 0;

    const mem = getUint8ArrayMemory0();

    let offset = 0;

    for (; offset < len; offset++) {
        const code = arg.charCodeAt(offset);
        if (code > 0x7F) break;
        mem[ptr + offset] = code;
    }
    if (offset !== len) {
        if (offset !== 0) {
            arg = arg.slice(offset);
        }
        ptr = realloc(ptr, len, len = offset + arg.length * 3, 1) >>> 0;
        const view = getUint8ArrayMemory0().subarray(ptr + offset, ptr + len);
        const ret = cachedTextEncoder.encodeInto(arg, view);

        offset += ret.written;
        ptr = realloc(ptr, len, offset, 1) >>> 0;
    }

    WASM_VECTOR_LEN = offset;
    return ptr;
}

function takeFromExternrefTable0(idx) {
    const value = wasm.__wbindgen_externrefs.get(idx);
    wasm.__externref_table_dealloc(idx);
    return value;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

const cachedTextEncoder = new TextEncoder();

if (!('encodeInto' in cachedTextEncoder)) {
    cachedTextEncoder.encodeInto = function (arg, view) {
        const buf = cachedTextEncoder.encode(arg);
        view.set(buf);
        return {
            read: arg.length,
            written: buf.length
        };
    };
}

let WASM_VECTOR_LEN = 0;

let wasmModule, wasmInstance, wasm;
function __wbg_finalize_init(instance, module) {
    wasmInstance = instance;
    wasm = instance.exports;
    wasmModule = module;
    cachedDataViewMemory0 = null;
    cachedInt32ArrayMemory0 = null;
    cachedUint16ArrayMemory0 = null;
    cachedUint32ArrayMemory0 = null;
    cachedUint8ArrayMemory0 = null;
    wasm.__wbindgen_start();
    return wasm;
}

async function __wbg_load(module, imports) {
    if (typeof Response === 'function' && module instanceof Response) {
        if (!module.ok) {
            throw new Error(`failed to fetch Wasm: ${module.status} ${module.statusText} fetching '${module.url}'`);
        }

        if (typeof WebAssembly.instantiateStreaming === 'function') {
            try {
                return await WebAssembly.instantiateStreaming(module, imports);
            } catch (e) {
                const validResponse = expectedResponseType(module.type);

                if (validResponse && module.headers.get('Content-Type') !== 'application/wasm') {
                    console.warn("`WebAssembly.instantiateStreaming` failed because your server does not serve Wasm with `application/wasm` MIME type. Falling back to `WebAssembly.instantiate` which is slower. Original error:\n", e);

                } else { throw e; }
            }
        }

        const bytes = await module.arrayBuffer();
        return await WebAssembly.instantiate(bytes, imports);
    } else {
        const instance = await WebAssembly.instantiate(module, imports);

        if (instance instanceof WebAssembly.Instance) {
            return { instance, module };
        } else {
            return instance;
        }
    }

    function expectedResponseType(type) {
        switch (type) {
            case 'basic': case 'cors': case 'default': return true;
        }
        return false;
    }
}

function initSync(module) {
    if (wasm !== undefined) return wasm;


    if (module !== undefined) {
        if (Object.getPrototypeOf(module) === Object.prototype) {
            ({module} = module)
        } else {
            console.warn('using deprecated parameters for `initSync()`; pass a single object instead')
        }
    }

    const imports = __wbg_get_imports();
    if (!(module instanceof WebAssembly.Module)) {
        module = new WebAssembly.Module(module);
    }
    const instance = new WebAssembly.Instance(module, imports);
    return __wbg_finalize_init(instance, module);
}

async function __wbg_init(module_or_path) {
    if (wasm !== undefined) return wasm;


    if (module_or_path !== undefined) {
        if (Object.getPrototypeOf(module_or_path) === Object.prototype) {
            ({module_or_path} = module_or_path)
        } else {
            console.warn('using deprecated parameters for the initialization function; pass a single object instead')
        }
    }

    if (module_or_path === undefined) {
        module_or_path = new URL('fk_sim_wasm_bg.wasm', import.meta.url);
    }
    const imports = __wbg_get_imports();

    if (typeof module_or_path === 'string' || (typeof Request === 'function' && module_or_path instanceof Request) || (typeof URL === 'function' && module_or_path instanceof URL)) {
        module_or_path = fetch(module_or_path);
    }

    const { instance, module } = await __wbg_load(await module_or_path, imports);

    return __wbg_finalize_init(instance, module);
}

export { initSync, __wbg_init as default };
